import React, { FormEvent, useState } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface SkillData {
  id: number
  name: string
  slug: string
  description: string | null
  content: string
}

interface AssignedAgent {
  id: number
  agent_id: number
  username: string
  agent_type: string
  slug: string
}

interface AgentOption {
  id: number
  username: string
  agent_type: string
}

interface Props {
  skill: SkillData
  agents: AssignedAgent[]
  all_agents: AgentOption[]
}

export default function Edit({ skill, agents, all_agents }: Props) {
  const [selectedAgentId, setSelectedAgentId] = useState<number | ''>('')

  const { data, setData, put, processing, errors } = useForm({
    skill: {
      name: skill.name,
      description: skill.description || '',
      content: skill.content,
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    put(`/manage/skills/${skill.id}`)
  }

  function handleDelete() {
    if (confirm(`Are you sure you want to delete skill '${skill.name}'? This cannot be undone.`)) {
      router.delete(`/manage/skills/${skill.id}`)
    }
  }

  function handleAssignAgent() {
    if (selectedAgentId) {
      router.post(`/manage/skills/${skill.id}/assign_agent`, { agent_id: selectedAgentId })
      setSelectedAgentId('')
    }
  }

  function handleRevokeAgent(agentSkill: AssignedAgent) {
    if (confirm(`Remove skill from '${agentSkill.username}'?`)) {
      router.delete(`/manage/skills/${skill.id}/revoke_agent/${agentSkill.id}`)
    }
  }

  return (
    <SidebarLayout section="manage" title={`Edit skill: ${skill.name}`}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 800,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={data.skill.name}
            onChange={e => setData('skill', { ...data.skill, name: e.target.value })}
            error={'skill.name' in errors}
            helperText={(errors as Record<string, string>)['skill.name']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={data.skill.description}
            onChange={e => setData('skill', { ...data.skill, description: e.target.value })}
            error={'skill.description' in errors}
            helperText={(errors as Record<string, string>)['skill.description']}
            sx={{ mb: 2.5 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            SKILL.md content (YAML frontmatter + markdown instructions)
          </Typography>
          <TextField
            label="Content"
            fullWidth
            multiline
            minRows={12}
            value={data.skill.content}
            onChange={e => setData('skill', { ...data.skill, content: e.target.value })}
            error={'skill.content' in errors}
            helperText={(errors as Record<string, string>)['skill.content']}
            sx={{ mb: 3, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Update skill
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/skills" style={{ color: '#1a1a1a' }}>Back to skills</Link>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>Assigned agents</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200, flexGrow: 1 }}>
            <InputLabel>Add agent</InputLabel>
            <Select
              value={selectedAgentId}
              label="Add agent"
              onChange={e => setSelectedAgentId(e.target.value as number)}
            >
              {all_agents.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.username} ({a.agent_type})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={handleAssignAgent} disabled={!selectedAgentId}>
            Assign
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>{agent.username}</TableCell>
                  <TableCell>
                    <Chip label={agent.agent_type} size="small" variant="outlined" color={agent.agent_type === 'machine' ? 'secondary' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" onClick={() => handleRevokeAgent(agent)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No agents have this skill assigned.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Danger zone
          </Typography>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={handleDelete}
          >
            Delete this skill
          </Button>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
